import type { DoBoxWorkKind } from './doBoxSignals';
import type { MessagingPlatform } from './messagingPlatform.enum';

export enum DoBoxDecisionKind {
  POLICY = 'POLICY',
  EPISODE = 'EPISODE',
  PERMISSION = 'PERMISSION',
  PERMISSION_OFFER = 'PERMISSION_OFFER',
}

export enum DoBoxDecisionStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  REVIEW_DUE = 'REVIEW_DUE',
  SUPERSEDED = 'SUPERSEDED',
}

export enum DoBoxDecisionOrigin {
  TYPED_VERBATIM = 'TYPED_VERBATIM',
  APPROVED_CARD = 'APPROVED_CARD',
  PATTERN_OFFER = 'PATTERN_OFFER',
  LEDGER_EXECUTED = 'LEDGER_EXECUTED',
}

export enum DoBoxDecisionScope {
  THREAD = 'THREAD',
  COUNTERPART = 'COUNTERPART',
  WORK_KIND = 'WORK_KIND',
  TASK_GROUP = 'TASK_GROUP',
  GLOBAL = 'GLOBAL',
}

export enum DoBoxDecisionFamily {
  SOURCE_REPLY = 'SOURCE_REPLY',
  OUTBOUND_MESSAGE = 'OUTBOUND_MESSAGE',
  CALENDAR_EVENT = 'CALENDAR_EVENT',
  DOBOX_INTERNAL = 'DOBOX_INTERNAL',
  CODE_ROUTE = 'CODE_ROUTE',
  NEVER_SUGGEST = 'NEVER_SUGGEST',
  ACCESS_DECLINED = 'ACCESS_DECLINED',
  DELEGATE_LOCAL_CODE = 'DELEGATE_LOCAL_CODE',
}

/**
 * The only three things a standing permission can ever cover. Deliberately has no
 * REPLY_DICTATED member: a standing permission covers a proposed time, a deadline-free ack
 * or an in-thread info request — never an event or a stated fact, which both need a human
 * look every time.
 */
export enum StandingPermissionClass {
  SCHEDULE_TIMES = 'SCHEDULE_TIMES',
  ACK_NO_DEADLINE = 'ACK_NO_DEADLINE',
  INFO_REQUEST_IN_THREAD = 'INFO_REQUEST_IN_THREAD',
}

/**
 * Email only, and only because a sent email has a real undo (recall / cancel-scheduled). No
 * SLACK_DM member: a Slack message is seen the instant it posts, so nothing there carries the
 * same UNDO guarantee a standing permission relies on.
 */
export enum StandingPermissionChannel {
  EMAIL = 'EMAIL',
}

/**
 * One row of the DoBoxDecisions table — PK userId, SK `sk` (`${kind}#${ulid}`). A POLICY is an
 * explicit, remembered instruction; an EPISODE is a captured outcome fed back from the ledger;
 * PERMISSION/PERMISSION_OFFER back the standing-permissions feature. The engine branches on
 * every field below, so all of them stay readable — only the user-written/model-composed text
 * seals.
 */
export interface DoBoxDecision {
  userId: string;
  sk: string;
  decisionId: string;
  kind: DoBoxDecisionKind;
  status: DoBoxDecisionStatus;
  origin: DoBoxDecisionOrigin;
  scope: DoBoxDecisionScope;
  family: DoBoxDecisionFamily;
  platform?: MessagingPlatform;
  workKind?: DoBoxWorkKind;
  // Structural: the quote was checked byte-for-byte against text the user actually typed in
  // the turn — the only path that can ever earn USER_SUPPLIED_PRIOR provenance at recall time.
  isVerbatimUserText: boolean;
  // Keyed hmacEmail/hmacPlatformIdentity digest of the counterpart — compared, never displayed.
  counterpartKeyHmac?: string;
  threadKeyHmac?: string;
  // Sparse GSI1 key, `${userId}#${counterpartKeyHmac}` — finds every decision tied to one
  // counterpart without a table scan.
  gsi1pk?: string;
  // Sparse GSI2 key — set only on a POLICY shared with a task-group's list.
  sharedTaskGroupId?: string;
  useCount: number;
  undoCount: number;
  createdAt: number;
  updatedAt: number;
  // Set on a POLICY only — the 180-day review cycle.
  reviewAt?: number;
  // Set on an EPISODE only (90 days); POLICY rows never expire this way.
  ttl?: number;
  // Set only on a PERMISSION/PERMISSION_OFFER row: which standing permission this is and the
  // one channel it is ever allowed to cover.
  actionClass?: StandingPermissionClass;
  channel?: StandingPermissionChannel;
  // SEALED (purpose chat_session_content, AAD { userId, decisionId, recordType }):
  /** The literal quote the user typed, for a POLICY whose isVerbatimUserText is true. */
  instructionText?: string;
  /** Server-composed summary shown on the decision's own management page. */
  decisionSummary?: string;
  /** JSON template of the remembered action — the schema itself rejects to/cc/bcc/threadId/
   *  fromAddress/accountId/channel/calendarEmail, so a destination can never be memorized. */
  actionTemplateJson?: string;
  counterpartLabel?: string;
}
